import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import Toast from 'react-native-toast-message';
import { deacService } from '../../../services/central-api/deac';
import MyDataTable from '../../../components/MyDataTable';
import AssignmentModal from './AssignmentModal';

interface ModalProps {
    selectedApplications: Presence[];
    setSelectedApplications: Dispatch<SetStateAction<Presence[]>>;
    presenceSituation: boolean;
}

export default function PresenceAssignmentModal({ selectedApplications, setSelectedApplications, presenceSituation }: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isAssignmentModalVisible, setIsAssignmentModalVisible] = useState(false);
    const [currentPresence, setCurrentPresence] = useState<Presence>();
    const [selectedAssignment, setSelectedAssignment] = useState<number>(0);

    async function vacancyApplicationAssignments() {
        try {
            setIsLoading(true);
            const resp = await deacService.getVacancyApplicationsAssignments({
                vacanciesApplicationIds: selectedApplications.map(a => a.id),
            });

            const updated = selectedApplications.map(sa => {
                const vacancyApplication = resp.find(x => x.vacancyApplicationId === sa.id);
                if (!vacancyApplication || !vacancyApplication.assignment) {
                    return sa;
                }

                return {
                    ...sa,
                    assignmentId: vacancyApplication.assignment.id,
                    assignment: vacancyApplication.assignment,
                };
            });
            setSelectedApplications(updated);
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (!presenceSituation) {
            return;
        }

        vacancyApplicationAssignments();
    }, [presenceSituation]);

    return (
        <View style={styles.cardList}>
            <MyDataTable
                columns={[
                    {
                        name: translate('agentName'),
                        cell: row => row.user.warName && row.user.warName != '' ? row.user.warName : row.user.name,
                        wrap: true
                    },
                    {
                        name: 'RF',
                        cell: row => row.user.registry,
                        wrap: true
                    },
                    {
                        name: translate('role'),
                        cell: row => row.user.role.name,
                        wrap: true
                    },
                    {
                        name: translate('vacancyId'),
                        cell: row => row.vacancy.id,
                        wrap: true
                    },
                    {
                        name: translate('activityTitle'),
                        cell: row => row.vacancy.title,
                        wrap: true
                    },
                    {
                        name: translate('workplace'),
                        cell: row => row.vacancy.workplace.name,
                        wrap: true
                    },
                    {
                        name: translate('agentActingBody'),
                        cell: row => row.user.actingBodyUnit.actingBody.name,
                        wrap: true
                    },
                    {
                        name: translate('agentUnit'),
                        cell: row => row.user.actingBodyUnit.name,
                        wrap: true
                    },
                    {
                        name: translate('assignment'),
                        cell: row => {
                            return <TouchableOpacity onPress={() => {
                                setCurrentPresence(row);
                                setSelectedAssignment(row.assignmentId ?? 0);
                                setIsAssignmentModalVisible(true);
                            }} style={{ width: '100%' }}>
                                <MyAppText style={{
                                    textDecorationLine: 'underline',
                                    color: theme.colors.labelColor,
                                    textDecorationColor: theme.colors.labelColor,
                                }}>{row.assignment?.name ?? translate('selectAssignment')}</MyAppText>
                            </TouchableOpacity>;
                        }
                    },
                ]}
                data={selectedApplications}
                progressPending={isLoading}
            />
            {currentPresence &&
                <AssignmentModal
                    currentPresence={currentPresence}
                    isAssignmentModalVisible={isAssignmentModalVisible}
                    setIsAssignmentModalVisible={setIsAssignmentModalVisible}
                    selectedApplications={selectedApplications}
                    setSelectedApplications={setSelectedApplications}
                    setSelectedAssignment={setSelectedAssignment}
                    selectedAssignment={selectedAssignment}
                />
            }
        </View>);
}

const styleSheet = createStyleSheet((theme) => ({
    cardList: {
        borderWidth: 1,
        borderRadius: 4,
        borderColor: theme.colors.neutral['200'],
        backgroundColor: theme.colors.dataTable.background,
        flex: 1,
    },
}));
